import {
  KdsDropfile,
  KdsText,
  KdsTextArea,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import { useContext, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useDirectUpload } from "@/hooks/useDirectUpload";

import { type InferredRefuseProofSchema } from "./ProofValidationActionsForm.schema";

export const ProofValidationActionsForm = () => {
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState(false);

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const methods = useFormContext<InferredRefuseProofSchema>();
  const { fields } = useFieldArray({
    control: methods.control,
    name: "versions",
  });

  const handleUpload = async (file: File, index: number) => {
    try {
      const uploadDetails = await createFileUploadUrl(
        "orderItem.deliveryNotes",
        file
      );
      const newSignedId = uploadDetails?.fileSignedId;

      pushFile(newSignedId, {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      });

      await uploadFile(file, uploadDetails);

      setFileUploadState(newSignedId, "uploaded");

      methods.setValue(`versions.${index}.fileSignedId`, newSignedId);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 422) {
          setFileUploadState(error.response?.data.fileSignedId, "error");
          methods.setError(`versions.${index}.fileSignedId`, {
            type: "manual",
            message: t("dropfile.errorDropfileType"),
          });
          methods.setValue(`versions.${index}.fileSignedId`, undefined);
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <KdsTextArea
        label={t(
          "order.myOrdersItemDetails.cardAction.fileProof.modal.form.refusalReason.label"
        )}
        {...methods.register("refusalReason")}
        autoFocus
        // ToDo allow classname or min-h on textarea DS2
        // className="w-full p-2 font-body text-sm outline-none"
        // rows={5}
        hasError={!!methods.formState.errors.refusalReason}
        errorMessage={t(
          "order.myOrdersItemDetails.cardAction.fileProof.modal.form.refusalReason.error"
        )}
      />

      {fields.map((field, index) => (
        <div className="flex flex-col gap-1" key={field.id}>
          <KdsText size="subtitle-sm">
            {t(
              "order.myOrdersItemDetails.cardAction.fileProof.modal.form.newDesign.label",
              {
                name: field.versionName,
              }
            )}
          </KdsText>

          <Controller
            name={`versions.${index}.fileSignedId`}
            render={({ field, fieldState }) => (
              <KdsDropfile
                dataCy="fileUploadForDeliveryNote"
                errorMessage={getDropfileErrorMessage(
                  fieldState.error?.message
                )}
                hasError={!!fieldState.error}
                isDeletable={!!field.value}
                isReadable={!!field.value}
                isLoading={isLoading}
                maxSize={SIZE_5G}
                onDelete={() => field.onChange(undefined)}
                onUpload={(value) => void handleUpload(value, index)}
                onUploadError={() =>
                  methods.setValue(`versions.${index}.fileSignedId`, undefined)
                }
                onUploadStart={() => {
                  setIsLoading(true);
                  methods.clearErrors(`versions.${index}.fileSignedId`);
                }}
                onUploadSuccess={() => setIsLoading(false)}
                placeholderDescription={t("dropfile.placeholderMaxSize5GB")}
                placeholderTitle={t("dropfile.placeholderTitle")}
              />
            )}
          />
        </div>
      ))}
    </div>
  );
};
